import { useState } from "react";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useListsContext } from "../ListsContext";
import { DragOverlayCard, ItemList } from "../Lists.styled";
import { ListItem } from "../types";
import { ListItemRow } from "./ListItemRow";

// The whole row is the drag handle, so a press that lands on a control has to be left
// alone or the pills, the icon buttons and the edit fields all stop working.
const INTERACTIVE = "button, input, select, textarea, label, a";

function startsOnInteractiveElement(event: Event) {
  const target = event.target as HTMLElement | null;
  return target?.closest(INTERACTIVE) != null;
}

// Replacing the stock activator means restating its right-click guard, which is the
// only other thing it checked.
class RowMouseSensor extends MouseSensor {
  static activators = [
    {
      eventName: "onMouseDown" as const,
      handler: ({ nativeEvent }: React.MouseEvent) =>
        nativeEvent.button !== 2 && !startsOnInteractiveElement(nativeEvent),
    },
  ];
}

class RowTouchSensor extends TouchSensor {
  static activators = [
    {
      eventName: "onTouchStart" as const,
      handler: ({ nativeEvent }: React.TouchEvent) =>
        !startsOnInteractiveElement(nativeEvent),
    },
  ];
}

// One row, wired to dnd-kit. Kept separate from ListItemRow so the row component stays
// about rendering an item and the floating copy can reuse it without its own sortable.
function SortableRow(props: { item: ListItem }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props.item.ItemId });

  return (
    <ListItemRow
      item={props.item}
      dragging={isDragging}
      rowRef={setNodeRef}
      rowStyle={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      rowHandlers={{ ...attributes, ...listeners }}
    />
  );
}

export function ListItems(props: { listId: string; items: ListItem[] }) {
  const { reorderItems } = useListsContext();
  const [activeId, setActiveId] = useState<string | null>(null);

  const ids = props.items.map((item) => item.ItemId);
  const activeItem =
    activeId == null
      ? null
      : props.items.find((item) => item.ItemId === activeId) ?? null;

  // Mouse activates after a short movement so a click still reads as a click. Touch
  // activates on a press-and-hold instead, which leaves a vertical swipe free to scroll
  // the page rather than picking up a row.
  const sensors = useSensors(
    useSensor(RowMouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(RowTouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function onDragStart(event: DragStartEvent) {
    setActiveId(String(event.active.id));
  }

  function onDragEnd(event: DragEndEvent) {
    setActiveId(null);
    const { active, over } = event;
    if (over == null || active.id === over.id) {
      return;
    }
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) {
      return;
    }
    reorderItems(props.listId, arrayMove(ids, from, to));
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <ItemList>
          {props.items.map((item) => (
            <SortableRow key={item.ItemId} item={item} />
          ))}
        </ItemList>
      </SortableContext>
      {/* The floating copy that follows the pointer. */}
      <DragOverlay dropAnimation={{ duration: 200, easing: "ease" }}>
        {activeItem != null && (
          <DragOverlayCard>
            <ListItemRow item={activeItem} />
          </DragOverlayCard>
        )}
      </DragOverlay>
    </DndContext>
  );
}
